// @ts-nocheck
import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  Download,
  Eye,
  Loader2,
  Lock,
  Pencil,
  Phone,
  Pause,
  Play,
  Search,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { c } from './ui';
import { AI_NAME_MAX_LENGTH } from '@/utilities/aiAgentsStore';

/* --------------------------------------------------------------- checklist */
export function Checklist({ items, goTo, attempted = true }) {
  return (
    <ul className={c('cl')}>
      {items.map((x) => {
        const bad = !x.done && !x.locked && (attempted || x.extra);
        return (
          <li key={x.id} className={c(x.done ? 'ok' : x.locked ? 'lk' : bad ? 'bad' : '')}>
            <span className={c('dot')}>
              {x.done ? <Check className="ags-i" /> : x.locked ? <Lock className="ags-i" /> : bad ? <X className="ags-i" /> : null}
            </span>
            <span className={c('t')}>
              <b>{x.label}</b>
              <small>{x.sub}</small>
            </span>
            {!x.done && (
              <button type="button" className={c('go')} onClick={() => goTo(x.id)}>
                {x.locked ? 'See why' : 'Fix'}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* ------------------------------------------------------------------ header */
export function Header({
  agentName,
  setAgentName,
  readOnly,
  typeLabel,
  statusPill,
  draftState,
  lastUpdatedLabel,
  canImport,
  onImport,
  onExport,
  onBack,
  isSuperAdmin,
  saving,
  onPublish,
  onSaveDraft,
  popOpen,
  setPopOpen,
  items,
  left,
  goTo,
}) {
  const popRef = useRef(null);
  const nameRef = useRef(null);
  useEffect(() => {
    if (!popOpen) return;
    const onDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setPopOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [popOpen]);

  const live = statusPill === 'Live';
  return (
    <div className={c('head')}>
      <button type="button" className={c('back')} aria-label="Back to agents" onClick={onBack}>
        <ArrowLeft className="ags-i" />
      </button>
      <div className={c('title', !readOnly && 'editable')}>
        <input
          ref={nameRef}
          value={agentName}
          readOnly={readOnly}
          aria-label="Agent name"
          title={readOnly ? undefined : 'Click to rename this agent'}
          placeholder="Name your agent"
          maxLength={AI_NAME_MAX_LENGTH}
          size={Math.min(Math.max((agentName || '').length, 12), 32)}
          onChange={(e) => setAgentName(e.target.value)}
        />
        {!readOnly && (
          <button
            type="button"
            className={c('editbtn')}
            aria-label="Rename agent"
            title="Rename agent"
            onClick={() => {
              nameRef.current?.focus();
              nameRef.current?.select();
            }}
          >
            <Pencil className="ags-i" />
          </button>
        )}
      </div>
      <span className={c('chip')}>{typeLabel}</span>
      <span className={c('chip', live ? 'pub' : 'draft')}>{live ? 'Published' : statusPill}</span>
      <span className={c('sp')} />
      <div className={c('actions')} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span
          aria-live="polite"
          className={c('saved')}
          style={draftState === 'error' ? { color: 'var(--red)' } : undefined}
        >
          {draftState === 'saving' && (
            <>
              <Loader2 className="ags-i" style={{ color: 'var(--muted)', animation: 'ags-spin 1s linear infinite' }} /> Saving…
            </>
          )}
          {draftState === 'saved' && (
            <>
              <Check className="ags-i" /> Autosaved {lastUpdatedLabel}
            </>
          )}
          {draftState === 'error' && <>Not saved. Retrying on your next change</>}
          {draftState === 'idle' && <>Last saved {lastUpdatedLabel}</>}
        </span>
        {canImport && (
          <button type="button" className={c('btn')} data-act="import" onClick={onImport}>
            <Upload className="ags-i" />
            Import JSON
          </button>
        )}
        <button type="button" className={c('btn')} data-act="export" onClick={onExport}>
          <Download className="ags-i" />
          Export JSON
        </button>
        {!isSuperAdmin && (
          <div className={c('pubwrap')} ref={popRef}>
            <button type="button" className={c('btn primary')} onClick={onPublish} disabled={saving}>
              {saving && <Loader2 className="ags-i" style={{ animation: 'ags-spin 1s linear infinite' }} />}
              {readOnly ? 'Edit' : saving ? 'Saving…' : 'Publish'}
              {!readOnly && !saving && left > 0 && <span className={c('badge')}>{left} left</span>}
            </button>
            {popOpen && !readOnly && left > 0 && (
              <div className={c('pop show')} role="dialog" aria-label="What is left before publishing">
                <h3>
                  {left} thing{left > 1 ? 's' : ''} left before you can publish
                </h3>
                <p>Agents need these to take calls. Tap Fix to jump there.</p>
                <Checklist items={items} goTo={goTo} />
                <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                  <button type="button" className={c('btn small')} onClick={onSaveDraft}>
                    Save draft
                  </button>
                  <button type="button" className={c('btn small ghost')} onClick={() => setPopOpen(false)}>
                    Keep editing
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function ViewBar({ show, onEdit }) {
  if (!show) return null;
  return (
    <div className={c('viewbar')}>
      <Eye className="ags-i" />
      You are viewing the published agent. Edit to make changes.
      <span className={c('sp')} />
      <button type="button" className={c('btn small keep')} onClick={onEdit}>
        Edit agent
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------- hero */
const BARS = [18, 34, 50, 26, 58, 40, 22, 48, 30, 54, 36, 20, 44, 28, 16];
export function Hero({ hero }) {
  const C = 2 * Math.PI * 36;
  const role = hero.role.length > 96 ? `${hero.role.slice(0, 96)}…` : hero.role;

  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const sampleUrl = hero.voiceSampleUrl || '';

  const stop = () => {
    const a = audioRef.current;
    if (a) {
      a.onended = null;
      a.onerror = null;
      a.pause();
      audioRef.current = null;
    }
    setPlaying(false);
  };
  useEffect(() => stop, []);
  useEffect(() => {
    stop();
  }, [sampleUrl]);

  const toggle = () => {
    if (playing) return stop();
    if (!sampleUrl) return;
    const a = new Audio(sampleUrl);
    audioRef.current = a;
    a.onended = stop;
    a.onerror = () => {
      stop();
      toast.error('Unable to play the voice sample');
    };
    setPlaying(true);
    a.play().catch(() => {
      stop();
      toast.error('Unable to play the voice sample');
    });
  };

  const bars = BARS.map((h, i) => (
    <i key={i} style={{ height: h, opacity: 0.35 + (i % 4) * 0.15, animationDelay: `${(i % 5) * 0.12}s` }} />
  ));

  return (
    <div className={c('hero')}>
      <div className={c('orb')}>{(hero.name || '?')[0].toUpperCase()}</div>
      <div>
        <h1>{hero.name || 'Your agent has no name yet'}</h1>
        <p>{role || 'Pick a persona and add a name to bring your agent to life.'}</p>
        <div className={c('meta')}>
          <span>{hero.persona || 'No persona yet'}</span>
          <span className={c(hero.voice ? '' : 'miss')}>{hero.voice ? `${hero.voice} voice` : 'No voice yet'}</span>
          {hero.language && <span>{hero.language}</span>}
        </div>
      </div>
      {sampleUrl ? (
        <button
          type="button"
          className={c('wave playable', playing && 'on')}
          onClick={toggle}
          aria-label={playing ? 'Stop voice sample' : `Play ${hero.voice || 'voice'} sample`}
          title={playing ? 'Stop voice sample' : 'Play voice sample'}
        >
          <span className={c('wplay')}>{playing ? <Pause className="ags-i" /> : <Play className="ags-i" />}</span>
          {bars}
        </button>
      ) : (
        <div className={c('wave')} aria-hidden="true">
          {bars}
        </div>
      )}
      <div className={c('pct')}>
        <div className={c('pctw')}>
          <svg viewBox="0 0 84 84">
            <circle cx="42" cy="42" r="36" fill="none" stroke="rgba(255,255,255,.18)" strokeWidth="7" />
            <circle
              cx="42"
              cy="42"
              r="36"
              fill="none"
              stroke={hero.left ? '#fff' : '#5FE0B0'}
              strokeWidth="7"
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={C * (1 - hero.done / hero.total)}
            />
          </svg>
          <b>
            {hero.done}/{hero.total}
          </b>
        </div>
        <small style={{ color: '#B8C4E6', fontSize: 11.5 }}>{hero.left ? `${hero.left} to go` : 'Ready'}</small>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ section list */
export function SectionNav({ formState, ctx, current, goTo, onSearch }) {
  return (
    <nav className={c('nav')} aria-label="Sections">
      <button type="button" className={c('search')} onClick={onSearch}>
        <Search className="ags-i" /> Jump to a section <span className={c('kbd')}>Ctrl K</span>
      </button>
      <div className={c('ngw')}>
        {GROUPS.map(([group, rows]) => (
          <div className={c('ng')} key={group}>
            <h4>{group}</h4>
            {rows.map(([id, label]) => {
              const [tone, text] = nodeInfo(id, formState, ctx);
              return (
                <button
                  key={id}
                  type="button"
                  className={c('ni', tone, current === id && 'cur')}
                  aria-current={current === id ? 'true' : undefined}
                  onClick={() => goTo(id)}
                >
                  <span className={c('d')}>
                    {tone === 'ok' ? <Check className="ags-i" /> : tone === 'bad' ? <AlertCircle className="ags-i" /> : null}
                  </span>
                  {label}
                  <small>{text}</small>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </nav>
  );
}

/* ------------------------------------------------------------- right panel */
function HearAgent({ form, data }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const voice = data.voices.items.find((v) => String(v.id) === String(form.voiceId));
  const greeting = form.personaData?.greeting?.trim();
  const name = form.personaData?.display_name?.trim();

  const stop = () => {
    const a = audioRef.current;
    if (a) {
      a.onended = null;
      a.onerror = null;
      a.pause();
      audioRef.current = null;
    }
    setPlaying(false);
  };
  useEffect(() => stop, []);
  useEffect(() => {
    stop();
  }, [form.voiceId]);

  const toggle = () => {
    if (playing) return stop();
    if (!voice?.sample_url) return;
    const a = new Audio(voice.sample_url);
    audioRef.current = a;
    a.onended = stop;
    a.onerror = () => {
      stop();
      toast.error('Unable to play the voice sample');
    };
    setPlaying(true);
    a.play().catch(() => {
      stop();
      toast.error('Unable to play the voice sample');
    });
  };

  return (
    <div className={c('card')}>
      <div className={c('who')}>
        <b>{(name || '?')[0].toUpperCase()}</b>
        <div>
          <h3>Hear your agent</h3>
          <div className={c('note')}>{voice ? `Voice: ${voice.name}` : 'No voice picked yet'}</div>
        </div>
      </div>
      {greeting ? <div className={c('bubble')}>{greeting}</div> : <div className={c('bubble empty')}>Your greeting will appear here as soon as you write it.</div>}
      {voice && (
        <button type="button" className={c('btn small')} onClick={toggle} disabled={!voice.sample_url}>
          {playing ? <Pause className="ags-i" /> : <Play className="ags-i" />}
          {playing ? 'Stop sample' : voice.sample_url ? 'Play voice sample' : 'No sample for this voice'}
        </button>
      )}
      <div className={c('note')} style={{ marginTop: 12 }}>
        {form.laya.enabled ? 'Common questions get an instant reply. Everything else goes to the AI.' : 'Every question goes to the AI. Fast answers are off.'}{' '}
        {form.humanHandoverEnabled ? 'Callers can ask for a person.' : 'The call ends instead of transferring to a person.'}
      </div>
    </div>
  );
}

export function Rail({ form, data, before, middle }) {
  return (
    <aside className={c('rail')} aria-label="Readiness">
      {before}
      {middle}

      <HearAgent form={form} data={data} />

      <div className={c('card')}>
        <h3>Test call</h3><p style={{ color: "orange" }}>upcoming.....</p>
        <p className={c('note')} style={{ margin: '6px 0 14px' }}>
          Talking to your agent from the browser is coming soon. For now, publish the agent and call its number to hear it.
        </p>
        <button type="button" className={c('btn')} disabled>
          <Phone className="ags-i" />
          Start test call
        </button>
      </div>
    </aside>
  );
}


export function Dock({ items, goTo, onPublish, show }) {
  if (!show) return null;
  const req = items.filter((x) => !x.extra);
  const done = req.filter((x) => x.done).length;
  const next = items.find((x) => !x.done && !x.locked) || items.find((x) => !x.done);
  return (
    <div className={c('dock')} style={{ display: undefined }}>
      <div className={c('dt')}>
        <b>
          {done} of {req.length} done
        </b>
        <small>{next ? `${next.label}: ${next.sub}` : 'Ready to publish'}</small>
      </div>
      {next ? (
        <button type="button" className={c('btn small')} onClick={() => goTo(next.id)}>
          Next
        </button>
      ) : (
        <button type="button" className={c('btn small')} onClick={onPublish}>
          Publish
        </button>
      )}
    </div>
  );
}
